/**
 * DocumentReviewSurface — customer-visible view of a quote or change order.
 *
 * PT-36 (2026-07-28). Consumed by (measured 2026-08-01):
 *   - SignaturePage.js (PT-37: client e-sign path)
 *   - RecordDecisionPage.js (PT-38: contractor records acceptance)
 *   - ChangeOrderReviewSend.js (CO review flow)
 *   - QuoteSelectionPanel.js (DEC-49: alternate/option selection)
 *   - SentQuoteView.js (§3c: read-only document on QuoteDetail)
 *
 * NOT consumed by QuoteReviewSend (predates this component by 6 weeks;
 * adoption never shipped). See COMPONENT_MAP.md for the measured list.
 *
 * Rule: a consumer list is measured, not aspirational. If a consumer is
 * intended but not wired, it says "intended", never "consumed by".
 *
 * Contract:
 *   - NO cost, NO margin, NO internal notes anywhere in the DOM (36.1, 36.7).
 *   - Alternates render as radio groups (36.3).
 *   - Options render as checkboxes (36.4).
 *   - Live total recomputes on any selection change (36.5).
 *   - Used by both SignaturePage and internal preview (36.6).
 *   - `readOnly` prop locks all selectors (accepted state).
 *
 * Props:
 *   documentType   'quote' | 'change_order'
 *   document       The full document object (quote or CO)
 *   selections     { alternates: [{group_id, chosen_line_id}], options: [{line_id, accepted}] }
 *   onSelectionChange(newSelections)  Callback when user changes a selection
 *   readOnly       boolean — locks all selectors (post-acceptance)
 *   children       Optional slot for the rendered document (QuoteDocument / CODocument)
 *   lineItems      Resolved line items (for total computation)
 *   totalCents     Server-derived total (when readOnly/accepted)
 *   computedTotal  Client-computed total (live, from selections)
 */

import React from 'react';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import QuoteSelectionPanel from './QuoteSelectionPanel';
import { formatCurrency } from '../../utils/formatCurrency';
import { acceptedAtOf, acceptedLine } from './acceptedLine';
// (formatMarginPct removed — not needed on the customer-facing surface)

export default function DocumentReviewSurface({
  documentType = 'quote',
  document,
  selections = { alternates: [], options: [] },
  onSelectionChange,
  readOnly = false,
  children,
  lineItems = [],
  computedTotal = 0,
  // IDI-370: optional { subtotal, discount, taxRate, tax, total } (cents).
  // When given, the customer sees how the total is built.
  totalsBreakdown = null,
  totalCents,
  // DEF-088 (W1.1c): distinguishes internal vs customer surface.
  //   customerView=false (default) — authenticated internal preview
  //     (RecordDecisionPage etc.); shows internal-tone DEF-081 wording
  //     and renders the total AS STORED for audit-trail visibility.
  //   customerView=true — customer-facing (SignaturePage token path);
  //     shows softer customer-tone copy AND suppresses the $ total row
  //     entirely when line items are missing (response §5 hard rule:
  //     never surface a $ total to a customer on a document whose line
  //     items are missing).
  customerView = false,
  // A2: show base lines as locked checkboxes in the scope block
  showBaseLines = false,
  // GP-11: suppress the standalone total card when the document already renders its own totals
  showTotal = true,
  // IDI-131: the host renders alternates under their parent lines itself.
  hideAlternates = false,
}) {
  // 36.5: the displayed total is either the locked server total (readOnly)
  // or the live client-computed total (interactive).
  const displayTotal = readOnly && totalCents != null ? totalCents : computedTotal;

  // IDI-544 / IDI-547: quotes and change orders share one two-state line —
  // e-signed or accepted manually, never a channel name.
  const acceptedAt = acceptedAtOf(document);

  // DEF-088 (W1.1c): detect the DEF-081-shape defect — the document has a
  // non-zero total but no line items backing it. Consider ANY positive
  // money signal:
  //   • client-computed total (live selection-driven)
  //   • server-derived total (readOnly / accepted-lock path)
  //   • document.subtotal / total_amount (stored on the record even when the
  //     customer-facing lineItems array is empty — this is the actual
  //     DEF-081 shape as it surfaces on the customer signature link)
  const hasLines = Array.isArray(lineItems) && lineItems.length > 0;
  const storedSubtotal = Number(document?.subtotal ?? document?.subtotal_cents ?? 0);
  // IDI-32: `bidAmountCents` was missing from this list, which is the ACTUAL
  // stored total on a BIDIT quote — there is no `total_amount` or `total_cents`
  // field on the record (verified against 46,974 quotes: `bidAmountCents` is
  // populated on 45,326 of them). With every candidate reading undefined,
  // positiveTotal was false, so `missingLines` was false, so the suppression
  // below never fired and the row rendered the computed total — $0.00 — for a
  // quote genuinely worth $26,094. A confident wrong price next to an
  // "Accept & Sign" button is worse than showing nothing.
  const storedTotal = Number(
    document?.bidAmountCents ??
      document?.bid_amount_cents ??
      document?.total_amount ??
      document?.total_cents ??
      document?.locked_total_cents ??
      document?.lockedTotalCents ??
      0
  );
  const positiveTotal =
    (Number.isFinite(displayTotal) && displayTotal > 0) ||
    (Number.isFinite(totalCents) && totalCents > 0) ||
    (Number.isFinite(storedSubtotal) && storedSubtotal > 0) ||
    (Number.isFinite(storedTotal) && storedTotal > 0);
  const missingLines = !hasLines && positiveTotal;

  return (
    <div className="space-y-6" data-testid="document-review-surface">
      {/* The rendered document (injected by consumer) */}
      {children}

      {/* Selections: shared QuoteSelectionPanel (DEC-49, customer mode) */}
      {!readOnly && (showBaseLines || (!hideAlternates && selections.alternates?.length > 0) || selections.options?.length > 0) && (
        <QuoteSelectionPanel
          lineItems={lineItems}
          selections={selections}
          onSelectionChange={onSelectionChange}
          mode="customer"
          showTotal={false}
          showBaseLines={showBaseLines}
          hideAlternates={hideAlternates}
        />
      )}

      {/* DEF-088 (W1.1c): inconsistency notice when total is present but no
          line items exist. Dual-tone by surface:
            • customerView=true  → customer-tone copy; total row SUPPRESSED.
            • customerView=false → internal-tone DEF-081 copy; total renders
              AS STORED for audit-trail visibility.
          Alert receives NO icon child — Alert owns its variant icon (DEF-090). */}
      {missingLines && customerView && (
        <div data-testid="document-review-surface-customer-missing-lines">
          <Alert variant="warning">
            This document is missing its line-item detail. Please contact the sender before signing.
          </Alert>
        </div>
      )}
      {missingLines && !customerView && (
        <div data-testid="document-review-surface-internal-missing-lines">
          <Alert variant="warning">
            This quote has a subtotal but no line items. The record may be incomplete — contact support before sending.
          </Alert>
        </div>
      )}

      {/* Live total (36.5). Suppressed on the customer surface when line
          items are missing (response §5 hard rule). */}
      {showTotal && !(missingLines && customerView) && (
        <Card className="p-4">
          {totalsBreakdown && !readOnly && (totalsBreakdown.discount > 0 || totalsBreakdown.tax > 0) && (
            <dl className="space-y-1 mb-3 text-sm" data-testid="totals-breakdown">
              <div className="flex justify-between">
                <dt style={{ color: 'var(--color-text-muted)' }}>Subtotal</dt>
                <dd className="tabular-nums" style={{ color: 'var(--color-text-default)' }}>{formatCurrency(totalsBreakdown.subtotal)}</dd>
              </div>
              {totalsBreakdown.discount > 0 && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--color-text-muted)' }}>Discount</dt>
                  <dd className="tabular-nums" style={{ color: 'var(--color-text-default)' }}>−{formatCurrency(totalsBreakdown.discount)}</dd>
                </div>
              )}
              {totalsBreakdown.tax > 0 && (
                <div className="flex justify-between">
                  <dt style={{ color: 'var(--color-text-muted)' }}>
                    Tax ({+(totalsBreakdown.taxRate * 100).toFixed(3)}%)
                  </dt>
                  <dd className="tabular-nums" style={{ color: 'var(--color-text-default)' }}>{formatCurrency(totalsBreakdown.tax)}</dd>
                </div>
              )}
            </dl>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {documentType === 'change_order' ? 'Change Order Total' : 'Quote Total'}
            </span>
            <span
              className="text-xl font-bold tabular-nums"
              style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-display)' }}
            >
              {formatCurrency(displayTotal)}
            </span>
          </div>
          {readOnly && acceptedAt && (
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
              {acceptedLine(document)}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
